/**
 * @module Link checks for the web sources a Flux Agent `search` card lists. The server already
 * sanitizes every URL (`flux_agent::web_search::safe_url`); this repeats the check at render time
 * so a stored or tampered card can never produce a `javascript:` or `data:` link.
 */

/** A source link that passed {@link sourceLink}. */
export interface SourceLink {
  /** Absolute http or https URL to put in `href`. */
  href: string;
  /** Host name shown beside the title, without a leading `www.`. */
  host: string;
}

/**
 * Accept only absolute http or https URLs with a host and no embedded credentials, at most 600
 * characters as on the server. Returns `null` for anything else, which the card shows as text.
 */
export function sourceLink(url: string): SourceLink | null {
  const trimmed = url.trim();
  if (!trimmed || trimmed.length > 600 || !/^https?:\/\//i.test(trimmed)) return null;
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
  if (!parsed.hostname || parsed.username || parsed.password) return null;
  return { href: parsed.href, host: parsed.hostname.replace(/^www\./, '') };
}
